import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  ArrowRight,
  AlertTriangle,
  Repeat,
  Eye,
  ArrowLeftRight,
  Brain,
  Sparkles,
  Zap,
  Waves,
  Anchor,
  Users,
  HeartHandshake,
  Shield,
  CreditCard,
  FileText,
  PhoneCall,
  Stethoscope,
  Flower,
  Workflow,
  BookOpen,
} from "lucide-react";
import SEO from "@/components/SEO";
import { usePageSeo } from "@/hooks/usePageSeo";
import CalendlyInlineBooking from "@/components/CalendlyInlineBooking";
import CareCreditBanner from "@/components/CareCreditBanner";

const PAGE_KEY = "disorganized-attachment-specialty";

const AREA_LINKS = [
  { id: "miami", to: "/specialties/online-ketamine-therapy-women-miami", label: "Miami, Florida" },
  { id: "cumberland", to: "/specialties/ketamine-therapy-hyper-independent-women-cumberland-maine", label: "Cumberland, Maine" },
  { id: "portland", to: "/specialties/ketamine-therapy-hyper-independent-women-portland-maine", label: "Portland, Maine" },
  { id: "dc", to: "/specialties/ketamine-therapy-hyper-independent-women-washington-dc", label: "Washington, D.C." },
];

const CALENDLY_URL =
  "https://calendly.com/divinelovewellnessenterprises/consultationmeeting";

const patterns = [
  {
    icon: Repeat,
    title: "The Push-Pull Dynamic",
    body: "Craving connection, then feeling overwhelmed or trapped as soon as someone gets close.",
  },
  {
    icon: Eye,
    title: "Hyper-Vigilance & Sabotage",
    body: "Constantly scanning your partner for signs of abandonment, rejection, or betrayal.",
  },
  {
    icon: ArrowLeftRight,
    title: "Emotional Swings",
    body: "Alternating between clinging for reassurance and suddenly shutting down or becoming emotionally distant.",
  },
  {
    icon: Brain,
    title: "Intellectualizing Your Trauma",
    body: "Knowing why you act this way intellectually, but feeling unable to stop your body's autonomic fight-flight-freeze response.",
  },
];

const kapMechanisms = [
  {
    icon: Shield,
    title: "Dissolves Defensive Walls",
    body: "Ketamine temporarily quietens the default mode network (DMN), softening rigid emotional defenses, fear, and hyper-vigilance so you can explore trauma safely without feeling overwhelmed.",
  },
  {
    icon: Zap,
    title: "Rapid Neuroplasticity",
    body: "Ketamine stimulates glutamate and AMPA receptors, boosting brain-derived neurotrophic factor (BDNF). This opens a critical window of neuroplasticity, allowing your brain to forge new neural pathways for secure attachment and trust.",
  },
  {
    icon: Waves,
    title: "Somatic & Subconscious Access",
    body: "Sublingual lozenge dosing helps you process stored relational fear directly from the body and subconscious mind, leading to rapid, sustained breakthroughs.",
  },
  {
    icon: Anchor,
    title: "Integration into Real-Life Security",
    body: "Guided integration sessions help anchor your ketamine insights into practical, embodied relational changes, turning temporary relief into permanent secure attachment.",
  },
];

const groupBenefits = [
  {
    icon: Users,
    title: "Relational Mirroring & Witnessing",
    body: "Experience emotional safety, empathy, and acceptance from peers who understand relational trauma.",
  },
  {
    icon: HeartHandshake,
    title: "Rewiring Isolation",
    body: "Directly dismantle the subconscious belief that you are alone or damaged.",
  },
  {
    icon: CreditCard,
    title: "Affordable Access",
    body: "Group cohorts offer a cost-effective path to deep psychedelic-assisted therapy.",
  },
];

const paymentOptions = [
  {
    icon: Shield,
    title: "Out-of-Network Coverage via Thrizer",
    body: "Full payment options are supported through Thrizer. Thrizer automatically processes your out-of-network insurance benefits, submits claims on your behalf, and helps you receive direct reimbursement or pay only your co-insurance out-of-pocket.",
  },
  {
    icon: CreditCard,
    title: "Full & Extended Payment Plans",
    body: "We offer flexible and extended payment plans to spread the cost of medical intakes, medication, and therapy sessions over time.",
  },
  {
    icon: FileText,
    title: "Superbills Available",
    body: "Comprehensive superbills are provided for all medical consultations and therapy sessions for manual submission to your insurance provider.",
  },
];

const journeySteps = [
  {
    icon: PhoneCall,
    title: "Free Candidacy Consultation",
    body: "Schedule a complimentary consultation to discuss your relational goals, questions, and eligibility for KAP.",
  },
  {
    icon: Stethoscope,
    title: "Medical Evaluation & Prescribing",
    body: "Partner with our medical team (Journey Clinical) for a virtual medical evaluation and prescription delivery directly to your home.",
  },
  {
    icon: Flower,
    title: "Preparation & KAP Dosing",
    body: "Prepare your intentions and complete your virtual dosing session with 1-on-1 therapist holding space and guiding your journey.",
  },
  {
    icon: Workflow,
    title: "Integration & Group Circles",
    body: "Translate your psychedelic insights into daily relationship practices through dedicated integration therapy.",
  },
];

const faqs = [
  {
    q: "Why is Ketamine effective for disorganized attachment style?",
    a: "Disorganized attachment stems from deep-seated fear and nervous system dysregulation. Ketamine temporarily lowers emotional defenses and enhances neuroplasticity, allowing you to reframe relational trauma and build feelings of safety without triggering fight-or-flight panic.",
  },
  {
    q: "Are group ketamine therapy sessions available?",
    a: "Yes! We offer small, intimate Ketamine-Assisted Group Psychotherapy cohorts. Group sessions allow participants to heal relational wounds in a supported community setting using combined mindfulness and integration practices.",
  },
  {
    q: "How can I pay for sessions using Thrizer or payment plans?",
    a: "We accept full payments through Thrizer, which automatically files out-of-network claims so you can maximize insurance reimbursement. Extended and flexible payment options are also available to fit your budget.",
  },
];

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 },
};

const SpecialtyDisorganizedAttachmentPage = () => {
  const seoOverride = usePageSeo(PAGE_KEY);

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const PrimaryCTA = ({ testId = "disorganized-attachment-cta" }) => (
    <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer">
      <Button
        size="lg"
        className="rounded-full px-8 py-6 bg-primary text-primary-foreground hover:bg-primary/90"
        data-testid={testId}
      >
        Book a Free Ketamine Candidacy Consultation
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </a>
  );

  return (
    <div className="pt-20" data-testid="specialty-disorganized-attachment-page">
      <SEO
        metaTitle={seoOverride?.meta_title}
        keywords={[seoOverride?.focus_keyword, ...(seoOverride?.secondary_keywords || [])]}
        title={
          seoOverride?.meta_title ||
          "Disorganized Attachment & KAP — Therapy for Fearful-Avoidant Women in FL, ME & DC"
        }
        description={
          seoOverride?.meta_description ||
          "Specialized Ketamine-Assisted Psychotherapy for high-functioning women with disorganized / fearful-avoidant attachment style. Break the push-pull cycle and rewire your nervous system for secure, safe intimacy in Florida, Maine, and Washington, D.C."
        }
        canonical="https://divinelovewellness.net/specialties/disorganized-attachment-kap"
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* === Hero === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-5">
              Specialty · Disorganized Attachment &amp; KAP
            </p>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-foreground leading-tight">
              Therapy for Disorganized-Attachment Style in Relationships for Women
            </h1>

            <h2 className="mt-6 font-serif text-xl sm:text-2xl lg:text-3xl italic font-medium text-primary leading-snug">
              Raise your hand if you&rsquo;re a high-functioning adult who craves emotional intimacy but fears closeness, experiences hyper-vigilance or &ldquo;fearful-avoidant&rdquo; patterns in relationships, and struggles to find breakthroughs through talk therapy alone. 🙋🏽‍♀️ I used to be there, so I get it.
            </h2>

            <h3 className="mt-6 font-serif text-lg sm:text-xl md:text-2xl font-semibold text-foreground leading-snug">
              Helping hyper-independent women heal their disorganized attachment style through the power of evidence-based Ketamine-Assisted Therapy.
            </h3>

            <figure className="mt-8" data-testid="disorganized-attachment-hero-image">
              <img
                src="https://customer-assets-rejwkqb3.emergentagent.net/job_833ff4cd-a791-443f-9fd4-023fdfa8f8c0/artifacts/ghugq55n_IMG_5951.jpeg"
                alt="An adult child and parent process disorganized attachment during a specialized therapy session, highlighting how Ketamine-Assisted Psychotherapy helps soften subconscious emotional defenses to heal deep-seated family trauma."
                loading="lazy"
                className="w-full rounded-2xl object-cover shadow-soft"
              />
              <figcaption className="mt-3 text-sm text-muted-foreground italic text-center">
                An adult child and parent process disorganized attachment during a specialized therapy session, highlighting how Ketamine-Assisted Psychotherapy helps soften subconscious emotional defenses to heal deep-seated family trauma.
              </figcaption>
            </figure>

            <p className="mt-8 text-base md:text-lg text-foreground/80 leading-[1.85]">
              Break free from the push-pull cycle of relational trauma. Rewire your brain for secure, safe intimacy through Ketamine-Assisted Psychotherapy (KAP) and specialized relational trauma care in Florida, Maine, and Washington, D.C.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <PrimaryCTA testId="disorganized-attachment-hero-cta" />
              <Link to="/contact">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full px-8 py-6 border-primary/30 text-primary hover:bg-primary/5"
                >
                  Ask Laura a Question
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === The Problem === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              The Problem
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              The Disorganized Attachment{" "}
              <span className="italic text-primary">Dilemma</span>
            </h2>
            <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-foreground/90 leading-snug">
              When Your Heart Craves Closeness, but Your Body Feels Danger
            </h3>

            <div className="mt-8 space-y-5 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                Disorganized attachment (often called{" "}
                <strong className="text-foreground">Fearful-Avoidant attachment</strong>) creates a painful paradox in intimate relationships.
              </p>
              <p>
                You deeply desire love, intimacy, and connection&mdash;yet when someone gets close, your nervous system responds with fear, panic, or the urge to pull away.
              </p>
              <p>
                This dynamic often develops from early relational trauma or inconsistent caregiving, teaching your subconscious mind that emotional closeness is unsafe.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === Patterns You May Recognize === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Recognize The Patterns
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Do You Recognize These{" "}
              <span className="italic text-primary">Relational Patterns?</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-4">
            {patterns.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 md:gap-5 p-5 md:p-6 bg-white border border-border/40 rounded-2xl"
                data-testid={`disorganized-attachment-pattern-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-accent/15 flex-shrink-0">
                  <item.icon className="h-5 w-5 text-accent" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>

          <motion.div
            {...fadeUp}
            className="mt-14 max-w-3xl mx-auto rounded-3xl border border-primary/15 bg-white/90 backdrop-blur shadow-soft px-6 md:px-10 py-8 md:py-10"
            data-testid="disorganized-attachment-areas-box"
          >
            <h2 className="font-serif text-xl sm:text-2xl md:text-3xl font-semibold text-foreground leading-snug text-center">
              Offering Virtual Ketamine-Assisted Therapy in these spots:
            </h2>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-3 text-base md:text-lg">
              {AREA_LINKS.map((area) => (
                <li key={area.id}>
                  <Link
                    to={area.to}
                    className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 decoration-primary/40 hover:decoration-primary transition-colors"
                    data-testid={`disorganized-attachment-area-link-${area.id}`}
                  >
                    {area.label}
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        </div>
      </section>

      {/* === The Science & Solution === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              The Science &amp; Solution
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Why Ketamine Is The{" "}
              <span className="italic text-primary">Breakthrough Answer</span>
            </h2>
            <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-foreground/90 leading-snug">
              Why Ketamine-Assisted Psychotherapy (KAP) Breaks the Relational Trauma Cycle
            </h3>

            <div className="mt-8 space-y-5 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                Talk therapy alone engages the conscious, analytical brain&mdash;but attachment trauma is stored deep within the body and implicit memory. When triggered, your brain&rsquo;s defense mechanisms lock down, making it difficult to access the emotional vulnerability needed to heal.
              </p>
              <p className="font-medium text-foreground">
                Ketamine acts as a powerful catalyst for relational healing:
              </p>
            </div>

            <ol className="mt-8 space-y-5">
              {kapMechanisms.map((step, idx) => (
                <li
                  key={step.title}
                  className="flex items-start gap-5 p-6 bg-accent/5 border border-accent/30 rounded-2xl"
                  data-testid={`disorganized-attachment-mechanism-${idx + 1}`}
                >
                  <div className="flex items-center justify-center h-11 w-11 rounded-full bg-primary text-primary-foreground font-serif text-lg font-semibold flex-shrink-0">
                    {idx + 1}
                  </div>
                  <div className="flex-1">
                    <h4 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                      <step.icon className="h-4 w-4 text-primary" />
                      {step.title}
                    </h4>
                    <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                      {step.body}
                    </p>
                  </div>
                </li>
              ))}
            </ol>
          </motion.div>
        </div>
      </section>

      {/* === Group & Individual KAP === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="mb-12">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Group &amp; Individual KAP
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Group &amp; Individual KAP{" "}
              <span className="italic text-primary">Sessions Available</span>
            </h2>
            <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-foreground/90 leading-snug">
              Heal in Community: Ketamine-Assisted Group Psychotherapy
            </h3>

            <div className="mt-6 space-y-5 text-base md:text-lg text-foreground/80 leading-[1.85]">
              <p>
                Because disorganized attachment is inherently a relational wound, healing often reaches its deepest potential within a safe, trauma-informed community.
              </p>
              <p>
                We offer both <strong className="text-foreground">1-on-1 Individual KAP</strong> and <strong className="text-foreground">Ketamine-Assisted Group Psychotherapy Cohorts</strong> combining Mindfulness-Based Cognitive Therapy (MBCT) and integration circles.
              </p>
              <p className="font-medium text-foreground">
                Why Group KAP for Attachment Healing?
              </p>
            </div>
          </motion.div>

          <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {groupBenefits.map((item) => (
              <Card
                key={item.title}
                className="p-6 md:p-7 bg-white border-border/40 rounded-2xl"
                data-testid={`disorganized-attachment-group-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-primary/10 inline-block">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <h3 className="font-serif text-lg font-semibold text-foreground mt-4">
                  {item.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground mt-2 leading-relaxed">
                  {item.body}
                </p>
              </Card>
            ))}
          </motion.div>

          <motion.div {...fadeUp} className="mt-10 text-center">
            <Link
              to="/specialties/ketamine-therapy-group-immersion-fl-me"
              className="inline-flex items-center gap-2 text-primary font-medium underline underline-offset-4 hover:text-primary/80 transition-colors"
              data-testid="disorganized-attachment-group-link"
            >
              Learn more about the 9-Week Group Immersion
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* === Investment & Payment === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp}>
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Investment, Insurance &amp; Flexible Payment
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Accessible, Transparent Payment &amp;{" "}
              <span className="italic text-primary">Out-of-Network Options</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-muted-foreground leading-relaxed">
              Healing your relationships should be accessible and stress-free. We provide clear pricing alongside flexible financial solutions so you can focus on your recovery.
            </p>
          </motion.div>

          <motion.div {...fadeUp} className="mt-10 space-y-4">
            {paymentOptions.map((item) => (
              <div
                key={item.title}
                className="flex items-start gap-4 md:gap-5 p-5 md:p-6 bg-secondary/30 border border-border/40 rounded-2xl"
                data-testid={`disorganized-attachment-payment-${item.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}
              >
                <div className="p-3 rounded-full bg-primary/10 flex-shrink-0">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-serif text-lg font-semibold text-foreground">
                    {item.title}
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {item.body}
                  </p>
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === 4-Step Journey === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-5xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              The Process
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              How the KAP Process{" "}
              <span className="italic text-primary">Works</span>
            </h2>
            <h3 className="mt-5 font-serif text-xl sm:text-2xl font-semibold text-foreground/90 leading-snug">
              Your 4-Step Journey to Secure Attachment
            </h3>
          </motion.div>

          <motion.ol {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {journeySteps.map((step, idx) => (
              <li
                key={step.title}
                className="flex items-start gap-5 p-6 bg-white border border-border/40 rounded-2xl"
                data-testid={`disorganized-attachment-journey-${idx + 1}`}
              >
                <div className="flex items-center justify-center h-11 w-11 rounded-full bg-primary text-primary-foreground font-serif text-lg font-semibold flex-shrink-0">
                  {idx + 1}
                </div>
                <div className="flex-1">
                  <h4 className="font-serif text-lg font-semibold text-foreground flex items-center gap-2">
                    <step.icon className="h-4 w-4 text-primary" />
                    {step.title}
                  </h4>
                  <p className="text-sm md:text-base text-muted-foreground mt-1 leading-relaxed">
                    {step.body}
                  </p>
                </div>
              </li>
            ))}
          </motion.ol>
        </div>
      </section>

      {/* === FAQ === */}
      <section className="py-24 md:py-32 px-6 md:px-12 bg-white">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeUp} className="text-center mb-14">
            <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold mb-4">
              Frequently Asked Questions
            </p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Disorganized Attachment &amp;{" "}
              <span className="italic text-primary">KAP</span>
            </h2>
          </motion.div>

          <motion.div {...fadeUp} className="space-y-5">
            {faqs.map((faq, idx) => (
              <Card
                key={idx}
                className="p-6 md:p-7 bg-secondary/30 border-border/40 rounded-2xl"
                data-testid={`disorganized-attachment-faq-${idx + 1}`}
              >
                <h3 className="font-serif text-lg md:text-xl font-semibold text-foreground">
                  {faq.q}
                </h3>
                <p className="mt-3 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {faq.a}
                </p>
              </Card>
            ))}
          </motion.div>
        </div>
      </section>

      {/* === Closing CTA === */}
      <section className="py-20 md:py-28 px-6 md:px-12 bg-secondary/30">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div {...fadeUp}>
            <Sparkles className="h-8 w-8 text-primary mx-auto mb-4" strokeWidth={1.5} />
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-semibold text-foreground leading-tight">
              Ready to Experience{" "}
              <span className="italic text-primary">Safe, Secure Intimacy?</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              You don&apos;t have to keep repeating the push-pull cycle. Take the first step toward emotional freedom, peace, and secure relationships.
            </p>

            <div className="mt-10 flex flex-wrap gap-4 justify-center">
              <PrimaryCTA testId="disorganized-attachment-closing-cta" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* === CareCredit Financing Banner === */}
      <CareCreditBanner variant="muted" />

      {/* === Inline Calendly Booking === */}
      <CalendlyInlineBooking background="bg-white" />
    </div>
  );
};

export default SpecialtyDisorganizedAttachmentPage;
